import { pickBy } from "lodash";

import {
  AgentAssignmentFilterEnum,
  AgentAssignmentFilterEnumMap,
  AttachmentsFilterEnumMap,
  Filter,
  IFilters,
  RatingFilter,
} from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { ensureArray, ensureFlat } from "helpers/data";
import {
  IDateRangeQueryString,
  deserializeCustomDateRangeURLParams,
  deserializeDateRangeURLParams,
  mapDateRangeToQueryString,
} from "helpers/filters";
import { filtersToOmitInURLForRoute, getRouteByPathname } from "helpers/tickets";
import { IKeyValue, stringifyQueryParams } from "helpers/url";
import { DetailedSource, SourceType, TicketSilo, TicketStatus } from "interfaces/entities/ticket";
import { ITicketSorting, TicketSortingField, TicketSortingOrder } from "interfaces/sorting";
import { ITicketSourceMapping } from "store/entities/application-settings/interfaces";
import {
  IWithTicketsViewState,
  getCurrentPage,
  getFilters,
  getRoute,
  getSearchQuery,
  getSilo,
  selectCurrentSorting,
} from "store/views/tickets/selectors";

import { ITicketsViewParams } from "./interfaces";

export function parseTicketsViewURLParams(
  queryParams: IKeyValue<string>,
  ticketSourceMapping: ITicketSourceMapping,
  pathname?: string
): ITicketsViewParams {
  const queryObject: ITicketsViewParams = {};

  if (queryParams.source) {
    const sources = ensureArray(queryParams.source) as SourceType[];
    const detailedSources = sources.flatMap(s => ticketSourceMapping[s] || []);

    if (detailedSources.length > 0) {
      queryObject[Filter.DetailedSource] = detailedSources;
    }
  }

  if (queryParams.detailedSource) {
    queryObject[Filter.DetailedSource] = ensureArray(queryParams.detailedSource) as DetailedSource[];
  }

  if (queryParams.assignee) {
    queryObject[Filter.Agent] = ensureArray(queryParams.assignee);
  }

  if (queryParams.assigned) {
    queryObject[Filter.AgentAssignment] = ensureArray(
      // @ts-expect-error TS(7053)
      AgentAssignmentFilterEnumMap.fromQueryString[ensureFlat(queryParams.assigned)] as AgentAssignmentFilterEnum[]
    );
  }

  if (queryParams.hasAttachments) {
    queryObject[Filter.Attachments] = ensureArray(
      // @ts-expect-error TS(7053)
      AttachmentsFilterEnumMap.fromQueryString[ensureFlat(queryParams.hasAttachments)]
    );
  }

  if (queryParams.group) {
    queryObject[Filter.Group] = ensureArray(queryParams.group);
  }

  if (queryParams.tag) {
    queryObject[Filter.Tag] = ensureArray(queryParams.tag);
  }

  if (queryParams.rate) {
    queryObject[Filter.Rating] = ensureArray(queryParams.rate) as RatingFilter[];
  }

  if (queryParams.priority) {
    if (queryParams.priority_op) {
      queryObject[Filter.Priority] = {
        priority: ensureArray(queryParams.priority).map(p => parseInt(p, 10)),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        priorityOp: ensureFlat(queryParams.priority_op) as any,
      };
    } else {
      queryObject[Filter.Priority] = { priority: ensureArray(queryParams.priority).map(p => parseInt(p, 10)) };
    }
  }

  if (queryParams.followers) {
    queryObject[Filter.Followers] = ensureArray(queryParams.followers);
  }

  const dateRangeFilter = deserializeCustomDateRangeURLParams(
    queryParams.date_from as string,
    queryParams.date_to as string
  );
  if (dateRangeFilter) {
    queryObject[Filter.DateRange] = dateRangeFilter;
  }

  const creationDateRangeFilter = deserializeCustomDateRangeURLParams(
    queryParams.creation_date_from as string,
    queryParams.creation_date_to as string
  );
  if (creationDateRangeFilter) {
    queryObject[Filter.CreationDateRange] = creationDateRangeFilter;
  }

  const lastActivityRangeFilter = deserializeCustomDateRangeURLParams(
    queryParams.last_activity_date_from as string,
    queryParams.last_activity_date_to as string
  );
  if (lastActivityRangeFilter) {
    queryObject[Filter.LastActivityDateRange] = lastActivityRangeFilter;
  }

  const lastMessageDateRangeFilter = deserializeCustomDateRangeURLParams(
    queryParams.last_message_from as string,
    queryParams.last_message_to as string
  );
  if (lastMessageDateRangeFilter) {
    queryObject[Filter.LastMessageDateRange] = lastMessageDateRangeFilter;
  }

  if (queryParams.status) {
    queryObject[Filter.TicketStatus] = ensureArray(queryParams.status) as TicketStatus[];
  }

  if (pathname) {
    const route = getRouteByPathname(pathname);
    if (route === TicketsRoutes.Search && queryParams.query) {
      queryObject.query = queryParams.query as string;
    }
    // @ts-expect-error TS(2322): Type 'TicketsRoutes | null' is not assignable to t... Remove this comment to see the full error message
    queryObject.route = route;
  }

  if (queryParams.page) {
    queryObject.page = parseInt(queryParams.page as string, 10);
  }

  if (queryParams.rate_date_from && queryParams.rate_date_to) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    queryObject[Filter.RatingDateRange] = deserializeDateRangeURLParams(
      queryParams.rate_date_from as string,
      queryParams.rate_date_to as string
    );
  }

  if (queryParams.resolution_date_from && queryParams.resolution_date_to) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    queryObject[Filter.ResolutionDateRange] = deserializeDateRangeURLParams(
      queryParams.resolution_date_from as string,
      queryParams.resolution_date_to as string
    );
  }

  if (queryParams.response_date_from && queryParams.response_date_to) {
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    queryObject[Filter.ResponseDateRange] = deserializeDateRangeURLParams(
      queryParams.response_date_from as string,
      queryParams.response_date_to as string
    );
  }

  if (queryParams.response_author) {
    queryObject[Filter.ResponseAuthor] = ensureArray(queryParams.response_author);
  }

  if (queryParams.silo) {
    queryObject.silo = queryParams.silo as TicketSilo;
  }

  if (queryParams.language) {
    queryObject[Filter.Language] = queryParams.language as string[];
  }

  if (queryParams.sortBy) {
    queryObject.sortBy = queryParams.sortBy as TicketSortingField;
  }

  if (queryParams.order) {
    queryObject.order = queryParams.order as TicketSortingOrder;
  }

  return queryObject;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function buildFiltersQueryParams(filters: IFilters) {
  let queryParams: IKeyValue<string | number | boolean> = {};

  const {
    agent,
    agentAssignment,
    creationDateRange,
    lastActivityDateRange,
    lastMessageDateRange,
    dateRange,
    group,
    rating,
    ratingDateRange,
    resolutionDateRange,
    responseDateRange,
    responseAuthor,
    tag,
    ticketStatus,
    priority,
    followers,
    detailedSource,
    hasAttachments,
    language,
  } = filters;

  if (agent) {
    queryParams.assignee = agent;
  }

  if (agentAssignment) {
    queryParams.assigned = AgentAssignmentFilterEnumMap.toQueryString[ensureFlat(agentAssignment)];
  }

  if (hasAttachments) {
    queryParams.hasAttachments = AttachmentsFilterEnumMap.toQueryString[ensureFlat(hasAttachments)];
  }

  if (group) {
    queryParams.group = group;
  }

  if (rating) {
    queryParams.rate = rating;
  }

  if (tag) {
    queryParams.tag = tag;
  }

  if (ticketStatus) {
    queryParams.status = ticketStatus;
  }

  if (responseAuthor) {
    queryParams.response_author = ensureFlat(responseAuthor);
  }

  if (detailedSource) {
    queryParams.detailedSource = detailedSource;
  }

  if (priority) {
    queryParams.priority = priority.priority;

    if (priority.priorityOp) {
      queryParams.priority_op = priority.priorityOp;
    }
  }

  if (followers) {
    queryParams.followers = followers;
  }

  if (language) {
    queryParams.language = language;
  }

  queryParams = {
    ...queryParams,
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(dateRange)),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(ratingDateRange), "rate"),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(resolutionDateRange), "resolution"),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(responseDateRange), "response"),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(creationDateRange), "creation"),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(lastActivityDateRange), "last_activity"),
    // @ts-expect-error TS(2345)
    ...buildDateRangeQueryString(mapDateRangeToQueryString(lastMessageDateRange), "last_message"),
  };
  return queryParams;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function buildSearchAndPageQueryParams(pathname: string, query: string, page: number, silo: TicketSilo) {
  const queryParams: IKeyValue<string> = {};

  if (pathname) {
    const route = getRouteByPathname(pathname);
    if (route === TicketsRoutes.Search && query) {
      queryParams.query = query;
      queryParams.silo = silo;
    }
  }

  if (page && page > 1) {
    queryParams.page = String(page);
  }

  return queryParams;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function buildDateRangeQueryString(params: IDateRangeQueryString, prefix?: string) {
  const propertyPrefix = prefix ? `${prefix}_` : "";
  return {
    ...(params.date_from && {
      [`${propertyPrefix}date_from`]: params.date_from,
    }),
    ...(params.date_to && {
      [`${propertyPrefix}date_to`]: params.date_to,
    }),
  };
}

export function buildSortingQueryString(sort: ITicketSorting): {
  sortBy: TicketSortingField;
  order: TicketSortingOrder;
} {
  return {
    sortBy: sort.field,
    order: sort.order,
  };
}

export function buildTicketsViewQueryString(state: IWithTicketsViewState, pathname?: string): string {
  const filters = getFilters(state);
  const query = getSearchQuery(state);
  const page = getCurrentPage(state);
  const route = getRoute(state);
  const silo = getSilo(state);
  const sort = selectCurrentSorting(state);

  const filtersToPutInQueryString = pickBy(
    filters,
    // @ts-expect-error TS(2769)
    (_, filterName: Filter) => !filtersToOmitInURLForRoute[route].includes(filterName)
  );

  const queryParams: IKeyValue<string | number | boolean> = {
    ...buildFiltersQueryParams(filtersToPutInQueryString),
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    ...buildSearchAndPageQueryParams(pathname, query, page, silo),
    ...buildSortingQueryString(sort),
  };

  return stringifyQueryParams(queryParams);
}
